import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { AppFrame } from "@/components/AppFrame";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Heading } from "@/components/primitives/Heading";
import { Hairline } from "@/components/primitives/Hairline";
import { LiveDot } from "@/components/primitives/LiveDot";
import { EventFeed, type FeedEvent } from "@/components/primitives/EventFeed";
import type { EvalEvent } from "@offsite/shared";

/**
 * Beacon Evaluating-Stage (`/workshop/evaluating`).
 * Brief 04 Screen 13 — VCLEvaluating.
 *
 * Eyebrow „EVALUATING · KI", Heading „Bewertung läuft.",
 * links Team-Liste mit Score-Status, rechts EventFeed Live-Stream.
 */

interface TeamScore {
  number: number;
  state: "pending" | "running" | "done" | "error";
  score?: number;
}

export function EvaluatingStage() {
  const { state } = useStore();
  const token = state.auth.token;
  const [, setProgress] = useState({ total: 0, completed: 0 });
  const [events, setEvents] = useState<EvalEvent[]>([]);
  const [scores, setScores] = useState<TeamScore[]>([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [progRes, evRes, evalRes, teamsRes] = await Promise.all([
          api.eval.progress(token),
          api.eval.events(token),
          api.evaluations.list(token),
          api.teams.list(token),
        ]);
        if (cancelled) return;
        setProgress(progRes);
        setEvents(evRes.events ?? []);

        const evalMap = new Map(
          (evalRes.evaluations ?? []).map((e) => [e.teamNumber, e]),
        );
        const next: TeamScore[] = teamsRes.teams.map((t) => {
          const ev = evalMap.get(t.number);
          if (ev) {
            return {
              number: t.number,
              state: "done",
              score: ev.score,
            };
          }
          return { number: t.number, state: "pending" };
        });
        // "running"-Heuristik: aus den letzten 10 Events Team-Tags lesen,
        // bei denen noch kein "saved"/"parsed" gekommen ist.
        const recent = (evRes.events ?? []).slice(-10);
        const runningTeams = new Set<number>();
        for (const e of recent) {
          if (e.type === "saved" || e.type === "parsed") {
            runningTeams.delete(e.teamNumber);
          } else {
            runningTeams.add(e.teamNumber);
          }
        }
        for (const row of next) {
          if (row.state === "pending" && runningTeams.has(row.number)) {
            row.state = "running";
          }
        }
        setScores(next);
      } catch {
        /* swallow */
      }
    };
    load();
    const id = setInterval(load, 1500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  const feedEvents: FeedEvent[] = events.slice(-12).map((e) => ({
    id: e.id,
    ts: e.ts,
    tag: `T-${String(e.teamNumber).padStart(2, "0")}`,
    message: e.message,
    ok: e.type === "saved" || e.type === "parsed",
  }));

  return (
    <AppFrame skin="presenter" hideHeader hideFooter>
      <section
        style={{
          flex: 1,
          padding: "var(--s-7)",
          display: "grid",
          gridTemplateColumns: "1fr 1.3fr",
          gap: "var(--s-7)",
          alignItems: "stretch",
        }}
      >
        {/* Links: Heading + Team-Score-Status */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-6)",
          }}
        >
          <header
            className="rise-1"
            style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}
          >
            <Eyebrow accent>EVALUATING · KI</Eyebrow>
            <Heading level="display">Bewertung läuft.</Heading>
          </header>
          <Hairline skin="presenter" />
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--s-3)",
              maxHeight: "55vh",
              overflow: "auto",
            }}
          >
            {scores.map((row) => (
              <li
                key={row.number}
                className="rise-2"
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: "var(--s-4)",
                }}
              >
                <LiveDot
                  live={row.state === "running"}
                  ok={row.state === "done"}
                />
                <span
                  className="mono"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    color: "var(--ink-300)",
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-meta)",
                  }}
                >
                  T-{String(row.number).padStart(2, "0")}
                </span>
                <span
                  className="mono"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 14,
                    color:
                      row.state === "done"
                        ? "var(--svo-weiss, #fff)"
                        : "var(--ink-400)",
                    textAlign: "right",
                  }}
                >
                  {row.state === "done" && row.score !== undefined
                    ? `${row.score.toFixed(1)} / 10`
                    : row.state === "running"
                      ? "···"
                      : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Rechts: Live-Event-Feed */}
        <aside
          className="rise-3"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-4)",
            background: "rgba(255,255,255,0.02)",
            padding: "var(--s-6)",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Eyebrow>LIVE STREAM</Eyebrow>
          <Hairline skin="presenter" />
          <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <EventFeed events={feedEvents} cursor skin="presenter" />
          </div>
        </aside>
      </section>
    </AppFrame>
  );
}

